import PropTypes from 'prop-types';
import React from 'react';
import Checkbox from 'components/connectedForm/CheckboxInput';
import { DragSource, DropTarget } from 'react-dnd';
import flow from 'lodash/flow';
import styles from 'styles/partials/_column-selector-option.css';
import styled from 'styled-components';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCalculator, fas } from '@fortawesome/free-solid-svg-icons';
import ColumnSelectorOptionContext from '../Context/ColumnSelectorOptionContext';

library.add(fas);
library.add(faCalculator);

const COLUMN_SELECTOR_OPTION = 'ColumnSelectorOption';

const CheckboxLabel = styled.div`
  display: inline-block;
  margin: 0;
  font-weight: normal;
  font-size: 14px;
  font-weight: 400;
  padding-left: 5px;
  vertical-align: text-bottom;
  white-space: nowrap;
  max-width: 100px;
  line-height: 23px;
  padding-top: 4px;
`;

const optionSource = {
  beginDrag(props) {
    return { fieldName: props.option.fieldName };
  },
  endDrag(props, monitor, component) {
    if (!monitor.didDrop()) {
      return;
    }

    let dropOn = monitor.getDropResult();
    dropOn.moveColumn(props.option.fieldName, dropOn.targetIndex);
  },
  canDrag(props, monitor) {
    return props.option.moveColumn && props.option.selected;
  },
};
const optionTarget = {
  drop(props, monitor, component) {
    return {
      moveColumn: props.option.moveColumn,
      targetIndex: props.index,
    };
  },
};

function collectTarget(connect, monitor) {
  return {
    dropTarget: connect.dropTarget(),
    canDrop: monitor.canDrop(),
    isDragOver: monitor.isOver(),
  };
}

function collectSource(connect, monitor) {
  return {
    dragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  };
}

export const ColumnSelectorOption = ({
  dragSource,
  dropTarget,
  glyph,
  highlight,
  index,
  option,
  selectOption,
  ...rest
}) => {
  // React generates a click event when the checked value of a checkbox is changed, consequently the bubbling of
  // all clicks is prevented and onChange calls selectOption directly - otherwise two click events would occur at the
  // SelectableList listener that would cancel each other out
  const { subTitle, description, selected, isNumeric, total, required } = option;

  if (subTitle) {
    let className = description ? styles['subTitle'] : styles['subDivider'];
    return <div className={className}>{description}</div>;
  }

  const onChange = () => {
    selectOption(option);
  };

  return dropTarget(
    dragSource(
      <div
        className={highlight ? styles['optionHighlight'] : styles['option']}
        onClick={(e) => {
          e.stopPropagation();
        }}
        data-testid={`filter_columns_list_${index}`}
      >
        <div style={{ display: 'flex' }}>
          <Checkbox
            inGrid={true}
            index={index}
            outerClass="listCheckboxInputGroup"
            label={description}
            checked={selected}
            value={selected}
            onChange={onChange}
            id={rest['data-testid']}
            data-testid={rest['data-testid']}
            readOnly={rest.listLabel === 'Filters' ? false : required}
          />
          <CheckboxLabel onClick={onChange}>{description}</CheckboxLabel>
          <span style={{ display: 'flex', width: '100%' }} />
          {glyph ? (
            <ColumnSelectorOptionContext.Provider value={{ selected, isNumeric, enabled: total, option }}>
              {glyph}
            </ColumnSelectorOptionContext.Provider>
          ) : null}
        </div>
      </div>
    )
  );
};

ColumnSelectorOption.propTypes = {
  option: PropTypes.shape({
    description: PropTypes.string.isRequired,
    selected: PropTypes.bool,
    fieldName: PropTypes.string.isRequired,
    moveColumn: PropTypes.func,
  }),
  highlight: PropTypes.bool,
  selectOption: PropTypes.func.isRequired,
  selectGlyph: PropTypes.func,
};

export default flow(
  DragSource(COLUMN_SELECTOR_OPTION, optionSource, collectSource),
  DropTarget(COLUMN_SELECTOR_OPTION, optionTarget, collectTarget)
)(ColumnSelectorOption);
